/**
 * Static "Ships by" data for Ring PRO.
 *
 * Source: the ops ETA sheet (Upal Das). The sheet is re-synced here every
 * ~2 days until the backend ETA API is live; then the API date wins and this
 * file becomes the fallback. Refresh = update RING_PRO_OPS_ETA_SHEET rows and
 * bump RING_PRO_OPS_ETA_AS_OF.
 *
 * The site shows the sheet's FIRST ETA (the in-stock promise). "Available
 * Units" are reference only — the website never counts orders against them;
 * the bi-daily refresh corrects drift. To force a thin-stock SKU onto a
 * longer promise between refreshes, add a rule to RING_PRO_SHIP_BY_OVERRIDES
 * (first match wins; same shape as DISABLED_RING_PRO_SKUS, plus offsetDays).
 */
import {
  RingProColors,
  RingSizes,
  ringProSkuRuleMatches,
  type DisabledRingProSkuRule,
  type RingProSkuQuery,
} from './constants';

export type RingProShipByRule = DisabledRingProSkuRule & {
  /** Calendar days from "today" to the shown ship-by date. */
  offsetDays: number;
} &
  // At least one discriminator is required: a bare `{ offsetDays }` rule
  // would match every SKU and silently bypass the whole sheet.
  (| Required<Pick<DisabledRingProSkuRule, 'colors'>>
    | Required<Pick<DisabledRingProSkuRule, 'sizes'>>
    | Required<Pick<DisabledRingProSkuRule, 'types'>>
    | Required<Pick<DisabledRingProSkuRule, 'stores'>>
  );

/** Fallback when nothing matches: OPEN size, or no size selected yet. */
export const RING_PRO_DEFAULT_SHIP_BY_OFFSET_DAYS = 21;

/** Manual overrides beat the sheet. Example (US Amazon FBA):
 *  { colors: [RingProColors.BLACK], sizes: [RingSizes.ELEVEN],
 *    stores: [ShopifyStore.US], offsetDays: 2 } */
export const RING_PRO_SHIP_BY_OVERRIDES: readonly RingProShipByRule[] = [];

export type RingProOpsEtaRow = {
  /** In-stock buffer backing the first ETA. Reference only, never counted here. */
  availableUnits?: number;
  /** Days promised while available units last — what the site shows. */
  firstEtaDays: number;
  /** Replenishment-based days once units are exhausted (backend will switch). */
  secondEtaDays: number;
};

export const RING_PRO_OPS_ETA_AS_OF = '2026-10-01';

type OpsSizeCode =
  | '05'
  | '06'
  | '07'
  | '08'
  | '09'
  | '10'
  | '11'
  | '12'
  | '13'
  | '14';
export type OpsSkuCode = `P${'A' | 'G' | 'R' | 'S'}${OpsSizeCode}`;

/** Ops SKU code = P + color letter (A Aster Black, G Bionic Gold,
 *  R Raw Titanium, S Space Silver) + 2-digit size. Mirrors the sheet 1:1.
 *  `satisfies` keeps the sheet exhaustive: a new size or colour fails the
 *  build here instead of silently falling through to the default offset. */
export const RING_PRO_OPS_ETA_SHEET = {
  PA05: { firstEtaDays: 30, secondEtaDays: 30 },
  PA06: { availableUnits: 5, firstEtaDays: 7, secondEtaDays: 30 },
  PA07: { firstEtaDays: 14, secondEtaDays: 14 },
  PA08: { firstEtaDays: 30, secondEtaDays: 30 },
  PA09: { firstEtaDays: 7, secondEtaDays: 7 },
  PA10: { firstEtaDays: 7, secondEtaDays: 7 },
  PA11: { availableUnits: 200, firstEtaDays: 7, secondEtaDays: 14 },
  PA12: { firstEtaDays: 30, secondEtaDays: 30 },
  PA13: { firstEtaDays: 30, secondEtaDays: 30 },
  PA14: { firstEtaDays: 30, secondEtaDays: 30 },
  PG05: { availableUnits: 31, firstEtaDays: 14, secondEtaDays: 30 },
  PG06: { availableUnits: 651, firstEtaDays: 14, secondEtaDays: 14 },
  PG07: { availableUnits: 179, firstEtaDays: 14, secondEtaDays: 14 },
  PG08: { firstEtaDays: 7, secondEtaDays: 7 },
  PG09: { availableUnits: 121, firstEtaDays: 14, secondEtaDays: 14 },
  PG10: { firstEtaDays: 7, secondEtaDays: 7 },
  PG11: { firstEtaDays: 7, secondEtaDays: 7 },
  PG12: { firstEtaDays: 30, secondEtaDays: 30 },
  PG13: { firstEtaDays: 30, secondEtaDays: 30 },
  PG14: { firstEtaDays: 30, secondEtaDays: 30 },
  PR05: { firstEtaDays: 30, secondEtaDays: 30 },
  PR06: { firstEtaDays: 30, secondEtaDays: 30 },
  PR07: { availableUnits: 180, firstEtaDays: 7, secondEtaDays: 14 },
  PR08: { firstEtaDays: 7, secondEtaDays: 7 },
  PR09: { availableUnits: 37, firstEtaDays: 14, secondEtaDays: 21 },
  PR10: { firstEtaDays: 30, secondEtaDays: 30 },
  PR11: { firstEtaDays: 30, secondEtaDays: 30 },
  PR12: { firstEtaDays: 30, secondEtaDays: 30 },
  PR13: { firstEtaDays: 30, secondEtaDays: 30 },
  PR14: { firstEtaDays: 30, secondEtaDays: 30 },
  PS05: { firstEtaDays: 30, secondEtaDays: 30 },
  PS06: { firstEtaDays: 30, secondEtaDays: 30 },
  PS07: { firstEtaDays: 30, secondEtaDays: 30 },
  PS08: { firstEtaDays: 30, secondEtaDays: 30 },
  PS09: { firstEtaDays: 30, secondEtaDays: 30 },
  PS10: { availableUnits: 45, firstEtaDays: 14, secondEtaDays: 14 },
  PS11: { firstEtaDays: 21, secondEtaDays: 21 },
  PS12: { availableUnits: 7, firstEtaDays: 21, secondEtaDays: 30 },
  PS13: { firstEtaDays: 30, secondEtaDays: 30 },
  PS14: { firstEtaDays: 30, secondEtaDays: 30 },
} satisfies Readonly<Record<OpsSkuCode, RingProOpsEtaRow>>;

/** String-indexed view for lookups built from runtime color/size values. */
const OPS_ETA_BY_CODE: Readonly<Record<string, RingProOpsEtaRow>> =
  RING_PRO_OPS_ETA_SHEET;

const OPS_COLOR_LETTER: Record<RingProColors, string> = {
  [RingProColors.BLACK]: 'A',
  [RingProColors.GOLD]: 'G',
  [RingProColors.TITANIUM]: 'R',
  [RingProColors.SILVER]: 'S',
};

function opsSkuCode(color: RingProColors, size: RingSizes): string {
  return `P${OPS_COLOR_LETTER[color]}${String(size).padStart(2, '0')}`;
}

export function getRingProShipByOffsetDays(
  query: RingProSkuQuery,
  overrides: readonly RingProShipByRule[] = RING_PRO_SHIP_BY_OVERRIDES,
): number {
  const override = overrides.find((rule) => ringProSkuRuleMatches(rule, query));
  if (override) return override.offsetDays;
  if (
    query.color != null &&
    query.size != null &&
    query.size !== RingSizes.OPEN
  ) {
    const row = OPS_ETA_BY_CODE[opsSkuCode(query.color, query.size)];
    if (row) return row.firstEtaDays;
  }
  return RING_PRO_DEFAULT_SHIP_BY_OFFSET_DAYS;
}
